import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { isAuthenticated, getUserType } from '../api/api';
import { MILAN_WORKSPACE_ENABLED, getBrandAppPath } from '../config/productFlags';
import './Home.css';

const WORKSPACE_FAQS = [
  {
    id: 1,
    question: "How does Milan AI's pricing work?",
    answer: "Milan AI uses a success-based pricing model. The platform is free to use — you only pay a 3.5% platform fee when your campaign is successfully completed. No monthly subscriptions, no upfront charges."
  },
  {
    id: 2,
    question: "Do I have to commit to a contract?",
    answer: "No commitment required! Milan AI operates on a completely flexible model. You can run one campaign or many — there are no contracts, lock-in periods, or minimum commitments. Start when you want, pause when you need to."
  },
  {
    id: 3,
    question: "How do I find the right creators for my brand?",
    answer: "Our AI-powered discovery engine helps you find Instagram creators based on niche, location, follower count, engagement rate, and estimated pricing. You can also get personalized recommendations based on your campaign goals."
  },
  {
    id: 4,
    question: "How are payments handled on the platform?",
    answer: "All payments are secured through our escrow system. When you run a campaign, funds are held safely until content is approved and performance is verified. This protects both brands and creators."
  },
  {
    id: 5,
    question: "Can I manage multiple campaigns simultaneously?",
    answer: "Yes! There's no limit to the number of campaigns you can run. Our dashboard lets you manage multiple campaigns, track different creators, and monitor performance across all your campaigns."
  },
  {
    id: 6,
    question: "What social media platforms do you support?",
    answer: "Currently, we support Instagram creators with verified business accounts. We analyze their engagement, content style, and audience to help you find the perfect match. YouTube and X (Twitter) integrations are coming soon!"
  },
  {
    id: 7,
    question: "How is Milan AI different from agencies?",
    answer: "Traditional agencies charge 15-30% commissions plus retainer fees. Milan AI charges only 3.5% on successfully completed campaigns — with direct access to creators, AI-powered matching, and built-in campaign management."
  },
  {
    id: 8,
    question: "Is my payment secure?",
    answer: "Absolutely. Our escrow system holds funds securely throughout the campaign. Fixed payments (60%) are released weekly during the monitoring period (1-4 weeks as you choose), and performance payments (40%) are released after monitoring completes — based on actual Instagram metrics."
  },
  {
    id: 9,
    question: "What is Performance Hub, and how is it different from running a campaign?",
    answer: "Performance Hub is a separate product for brands who already have a list of creators and post URLs. You upload a CSV, we send connect links, and metrics sync to your dashboard — without going through Milan's full campaign, invitation, content approval, or escrow payment flow. Use end-to-end campaigns when you want discovery and payouts on-platform; use Performance Hub when you only need Instagram measurement and lightweight coordination for your own roster."
  }
];

const PERF_HUB_FAQS = [
  {
    id: 1,
    question: "What is Performance Hub?",
    answer: "Performance Hub lets brands and agencies upload the creators and Instagram posts they already work with, invite creators to connect Instagram once via magic link, and track verified metrics — likes, comments, reach, saves, and more — in one dashboard."
  },
  {
    id: 2,
    question: "Do creators need a Milan AI account?",
    answer: "No. Creators receive an email magic link, connect Instagram once, and never need to sign up for Milan. You keep outreach and payments in your own tools."
  },
  {
    id: 3,
    question: "How does pricing work?",
    answer: "Performance Hub has a Free tier for small teams, plus Pro and Enterprise monthly plans for larger rosters and CSV export. There is no escrow and no campaign success fee on Performance Hub."
  },
  {
    id: 4,
    question: "Is this a replacement for my agency workflow?",
    answer: "No — it's a measurement layer behind the roster you already run. Upload handles, emails, and post URLs; we handle Instagram connect and metric sync so you can see CPM, engagement, and top performers clearly."
  },
  {
    id: 5,
    question: "What do I need to get started?",
    answer: "A brand account, a CSV with Instagram handle, email, and post URL per row, and creators who can accept the Instagram connect email. Metrics appear after they connect."
  },
  {
    id: 6,
    question: "Which platforms are supported?",
    answer: "Instagram is live today for Performance Hub. YouTube and X tracking are on the roadmap."
  }
];

function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const canFindCreators = MILAN_WORKSPACE_ENABLED && isAuthenticated() && getUserType() === 'brand';
  const brandAppPath = getBrandAppPath();
  const loggedIn = isAuthenticated();
  const faqs = MILAN_WORKSPACE_ENABLED ? WORKSPACE_FAQS : PERF_HUB_FAQS;
  const primaryCta = MILAN_WORKSPACE_ENABLED
    ? (canFindCreators ? '/find-creators' : '/signup')
    : (loggedIn && getUserType() === 'brand' ? '/performance' : '/signup');
  const primaryCtaLabel = MILAN_WORKSPACE_ENABLED
    ? (canFindCreators ? 'Find Creators' : 'Start Free Campaign')
    : (loggedIn && getUserType() === 'brand' ? 'Open Performance Hub' : 'Start free tracking');

  return (
    <div className="home-page">
      <Navbar />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-container">
          {MILAN_WORKSPACE_ENABLED ? (
            <>
              <div className="trust-badge">
                <span className="badge-icon">🤖</span>
                India's First AI-Powered Influencer Marketing Platform
              </div>
              <h1 className="hero-title">
                Pay creators based on<br />
                <span className="gradient-text">actual performance</span>
              </h1>
              <p className="hero-subtitle">
                The only platform where creator payments are tied to <strong>real Instagram metrics</strong>.
                AI-powered matching, escrow protection, and human validation — built for modern brands.
              </p>
              <div className="hero-cta">
                <Link to={primaryCta} className="btn-primary">
                  {primaryCtaLabel}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
                <Link to="/pricing" className="btn-secondary">View Pricing</Link>
              </div>
              <div className="hero-mockup" aria-hidden="true">
                <div className="hero-mockup-frame">
                  <div className="hero-mockup-chrome">
                    <span className="hero-mockup-dot red" />
                    <span className="hero-mockup-dot yellow" />
                    <span className="hero-mockup-dot green" />
                    <span className="hero-mockup-url">milan.ai / brand-dashboard</span>
                  </div>
                  <div className="hero-mockup-body">
                    <div className="hero-mockup-row">
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Active campaigns</span>
                        <span className="hero-mockup-stat-value">12</span>
                      </div>
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Tracked creators</span>
                        <span className="hero-mockup-stat-value">438</span>
                      </div>
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Avg. engagement</span>
                        <span className="hero-mockup-stat-value accent-green">6.2%</span>
                      </div>
                    </div>
                    <div className="hero-mockup-chart" role="img" aria-label="Mock engagement trend chart">
                      <span className="hero-mockup-bar" style={{ height: '38%' }} />
                      <span className="hero-mockup-bar" style={{ height: '52%' }} />
                      <span className="hero-mockup-bar" style={{ height: '44%' }} />
                      <span className="hero-mockup-bar" style={{ height: '68%' }} />
                      <span className="hero-mockup-bar" style={{ height: '60%' }} />
                      <span className="hero-mockup-bar" style={{ height: '82%' }} />
                      <span className="hero-mockup-bar tall" style={{ height: '94%' }} />
                    </div>
                  </div>
                </div>
                <span className="hero-mockup-floater campaigns" aria-hidden="true">
                  <span className="hero-mockup-floater-dot" /> Campaign payouts on track
                </span>
                <span className="hero-mockup-floater perfhub" aria-hidden="true">
                  <span className="hero-mockup-floater-dot green" /> Performance Hub syncing
                </span>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-value">Performance</span>
                  <span className="stat-label">Based Payments</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-value">AI + Human</span>
                  <span className="stat-label">Validation</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-value">3.5%</span>
                  <span className="stat-label">Only on Success</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="trust-badge">
                <span className="badge-icon">📊</span>
                Performance Hub · Live for brands &amp; agencies
              </div>
              <h1 className="hero-title">
                Track Instagram performance<br />
                <span className="gradient-text">for creators you already work with</span>
              </h1>
              <p className="hero-subtitle">
                Upload your roster, send one-click Instagram connect links, and see verified metrics,
                economics, and top performers — <strong>without replacing your agency workflow</strong>.
              </p>
              <div className="hero-cta">
                <Link to={primaryCta} className="btn-primary">
                  {primaryCtaLabel}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
                <Link to="/pricing" className="btn-secondary">See plans</Link>
              </div>
              <div className="hero-mockup" aria-hidden="true">
                <div className="hero-mockup-frame">
                  <div className="hero-mockup-chrome">
                    <span className="hero-mockup-dot red" />
                    <span className="hero-mockup-dot yellow" />
                    <span className="hero-mockup-dot green" />
                    <span className="hero-mockup-url">milan-ai.net / performance</span>
                  </div>
                  <div className="hero-mockup-body">
                    <div className="hero-mockup-row">
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Tracked posts</span>
                        <span className="hero-mockup-stat-value">48</span>
                      </div>
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Connected</span>
                        <span className="hero-mockup-stat-value accent-green">36</span>
                      </div>
                      <div className="hero-mockup-stat">
                        <span className="hero-mockup-stat-label">Avg. ER</span>
                        <span className="hero-mockup-stat-value accent-green">5.8%</span>
                      </div>
                    </div>
                    <div className="hero-mockup-chart" role="img" aria-label="Mock engagement trend chart">
                      <span className="hero-mockup-bar" style={{ height: '38%' }} />
                      <span className="hero-mockup-bar" style={{ height: '52%' }} />
                      <span className="hero-mockup-bar" style={{ height: '44%' }} />
                      <span className="hero-mockup-bar" style={{ height: '68%' }} />
                      <span className="hero-mockup-bar" style={{ height: '60%' }} />
                      <span className="hero-mockup-bar" style={{ height: '82%' }} />
                      <span className="hero-mockup-bar tall" style={{ height: '94%' }} />
                    </div>
                  </div>
                </div>
                <span className="hero-mockup-floater perfhub" aria-hidden="true">
                  <span className="hero-mockup-floater-dot green" /> Metrics syncing daily
                </span>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-value">CSV upload</span>
                  <span className="stat-label">Your roster</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-value">Magic link</span>
                  <span className="stat-label">Instagram connect</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-value">From ₹0</span>
                  <span className="stat-label">Free tier available</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Two-product chooser — Milan Workspace builds only */}
      {MILAN_WORKSPACE_ENABLED && (
        <section className="product-paths-section" aria-labelledby="product-paths-heading">
          <div className="section-container">
            <div className="product-paths-header">
              <span className="product-paths-eyebrow">TWO PRODUCTS · ONE PLATFORM</span>
              <h2 id="product-paths-heading">Pick the path that fits your workflow</h2>
              <p>Milan AI ships two separate products. Choose the one that matches what you need today — you can use both.</p>
            </div>

            <div className="product-paths-grid">
              <article className="path-card path-card--campaigns">
                <span className="path-card-ribbon path-card-ribbon--soon" aria-label="Launching soon">
                  Launching soon
                </span>
                <header className="path-card-head">
                  <span className="path-card-tag">
                    MILAN CAMPAIGNS
                    <span className="path-card-soon" aria-label="Launching soon">SOON</span>
                  </span>
                  <div className="path-card-icon path-card-icon--campaigns" aria-hidden="true">🚀</div>
                  <h3>Run a full campaign on Milan</h3>
                  <p className="path-card-use">
                    <strong>Use this when:</strong> you need to discover creators, brief them, hold funds in escrow,
                    and pay based on real performance — all on-platform.
                  </p>
                </header>
                <ul className="path-card-bullets">
                  <li><span className="path-card-check">✓</span> AI-powered discovery &amp; recommendations</li>
                  <li><span className="path-card-check">✓</span> Brief, invitations, content approvals (≤3 revisions)</li>
                  <li><span className="path-card-check">✓</span> Escrow payments — 60% weekly fixed + 40% performance</li>
                  <li><span className="path-card-check">✓</span> Live Instagram metrics &amp; dispute resolution</li>
                </ul>
                <div className="path-card-soon-note" role="note">
                  <span aria-hidden="true">🛠️</span>
                  <div>
                    <strong>Launching soon.</strong> Sign up now to preview the flow, save creators, and get notified
                    the moment campaigns go live.
                  </div>
                </div>
                <div className="path-card-price">
                  <span className="path-card-price-amount">Free</span>
                  <span className="path-card-price-sub">platform access · <strong>3.5%</strong> only on completed campaigns (at launch)</span>
                </div>
                <Link
                  to={canFindCreators ? "/find-creators" : "/signup"}
                  className="path-card-cta path-card-cta--campaigns"
                >
                  {canFindCreators ? "Explore the preview" : "Get early access"}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </article>

              <article className="path-card path-card--perfhub">
                <header className="path-card-head">
                  <span className="path-card-tag perfhub">
                    PERFORMANCE HUB
                    <span className="path-card-live" aria-label="Live feature">LIVE</span>
                  </span>
                  <div className="path-card-icon path-card-icon--perfhub" aria-hidden="true">📊</div>
                  <h3>Track creators you already have</h3>
                  <p className="path-card-use">
                    <strong>Use this when:</strong> you already have a list of creators and post URLs and just need
                    Instagram metrics — <em>no escrow, no creator payments handled by Milan</em>.
                  </p>
                </header>
                <ul className="path-card-bullets">
                  <li><span className="path-card-check check-emerald">✓</span> CSV upload (handle, email, post URL)</li>
                  <li><span className="path-card-check check-emerald">✓</span> Creators connect Instagram via magic link</li>
                  <li><span className="path-card-check check-emerald">✓</span> Daily metric sync + top-performer leaderboard</li>
                  <li><span className="path-card-check check-emerald">✓</span> Private CRM &amp; CSV export (paid tiers)</li>
                </ul>
                <div className="path-card-price">
                  <span className="path-card-price-amount">From ₹0</span>
                  <span className="path-card-price-sub">Pro <strong>₹1,000/mo</strong> · Enterprise <strong>₹2,500/mo</strong></span>
                </div>
                <Link to="/performance" className="path-card-cta path-card-cta--perfhub">
                  Open Performance Hub
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </article>
            </div>

            <div className="product-paths-cheatsheet" role="table" aria-label="Quick differences between Milan campaigns and Performance Hub">
              <div className="cheatsheet-row cheatsheet-head" role="row">
                <div role="columnheader">If you want to…</div>
                <div role="columnheader" className="col-campaigns">
                  Milan Campaigns
                  <span className="cheatsheet-soon-pill" aria-label="Launching soon">SOON</span>
                </div>
                <div role="columnheader" className="col-perfhub">
                  Performance Hub
                  <span className="cheatsheet-live-pill" aria-label="Live feature">LIVE</span>
                </div>
              </div>
              <div className="cheatsheet-row" role="row">
                <div role="cell">Discover &amp; invite new creators</div>
                <div role="cell" className="cell-yes">✓ Built-in</div>
                <div role="cell" className="cell-no">— Bring your own list</div>
              </div>
              <div className="cheatsheet-row" role="row">
                <div role="cell">Hold money in escrow &amp; pay creators</div>
                <div role="cell" className="cell-yes">✓ Escrow + 60/40 payout</div>
                <div role="cell" className="cell-no">— Not handled by Milan</div>
              </div>
              <div className="cheatsheet-row" role="row">
                <div role="cell">Just track Instagram metrics on posts</div>
                <div role="cell" className="cell-partial">Included in active campaigns</div>
                <div role="cell" className="cell-yes-emerald">✓ The whole point</div>
              </div>
              <div className="cheatsheet-row" role="row">
                <div role="cell">Pricing model</div>
                <div role="cell">3.5% success fee per campaign</div>
                <div role="cell">Monthly subscription (free tier available)</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Performance Hub deep-dive — always shown */}
      <section className="perf-hub-home-section" aria-labelledby="perf-hub-home-title">
        <div className="section-container perf-hub-home-inner">
          <div className="perf-hub-home-copy">
            <span className="perf-hub-home-badge">
              PERFORMANCE HUB · {MILAN_WORKSPACE_ENABLED ? 'DEEP DIVE' : 'HOW IT WORKS'}
              <span className="perf-hub-home-live">LIVE</span>
            </span>
            <h2 id="perf-hub-home-title">
              {MILAN_WORKSPACE_ENABLED
                ? 'Already have your creators? Just track them.'
                : 'Bring a CSV. Get Instagram metrics you can trust.'}
            </h2>
            <p className="perf-hub-home-lead">
              Bring a CSV. We email each creator a one-click Instagram connect link — no Milan signup needed for them.
              Metrics flow into your dashboard within minutes, and you keep working in your own tools for payments and outreach.
            </p>
            <ul className="perf-hub-home-bullets">
              <li><span className="perf-hub-bullet-check">✓</span> CSV import &amp; email invites (rate-limited, opt-out honoured)</li>
              <li><span className="perf-hub-bullet-check">✓</span> Magic-link Instagram OAuth — creator stays in control</li>
              <li><span className="perf-hub-bullet-check">✓</span> Daily auto-sync · likes, comments, plays, reach, saves</li>
              <li><span className="perf-hub-bullet-check">✓</span> Top performers, leaderboard &amp; CSV export on paid tiers</li>
              <li><span className="perf-hub-bullet-check">✓</span> Private CRM shortlist — only inside Performance Hub</li>
            </ul>
            <div className="perf-hub-home-ctas">
              <Link to="/performance" className="btn-perf-hub-solid">
                Open Performance Hub
              </Link>
              <Link to="/pricing#estimator-heading" className="btn-perf-hub-outline">
                See plans &amp; pricing
              </Link>
              <Link to="/services" className="perf-hub-home-services-link">
                Read full feature details →
              </Link>
            </div>
          </div>

          <div className="perf-hub-preview" aria-hidden="true">
            <div className="perf-hub-preview-frame">
              <div className="perf-hub-preview-head">
                <span className="perf-hub-preview-dot" />
                <span className="perf-hub-preview-title">Spring Drop · Performance Hub</span>
                <span className="perf-hub-preview-status">syncing</span>
              </div>
              <div className="perf-hub-preview-rows">
                <div className="perf-hub-preview-row">
                  <span className="perf-hub-preview-handle">@maya.styles</span>
                  <span className="perf-hub-preview-metric">❤️ 12.4k</span>
                  <span className="perf-hub-preview-metric">💬 842</span>
                  <span className="perf-hub-preview-pill connected">connected</span>
                </div>
                <div className="perf-hub-preview-row">
                  <span className="perf-hub-preview-handle">@arjun.foodie</span>
                  <span className="perf-hub-preview-metric">❤️ 8.1k</span>
                  <span className="perf-hub-preview-metric">💬 615</span>
                  <span className="perf-hub-preview-pill connected">connected</span>
                </div>
                <div className="perf-hub-preview-row">
                  <span className="perf-hub-preview-handle">@kavya.fit</span>
                  <span className="perf-hub-preview-metric">❤️ 5.3k</span>
                  <span className="perf-hub-preview-metric">💬 380</span>
                  <span className="perf-hub-preview-pill pending">awaiting</span>
                </div>
                <div className="perf-hub-preview-row">
                  <span className="perf-hub-preview-handle">@rhea.travels</span>
                  <span className="perf-hub-preview-metric">❤️ 19.7k</span>
                  <span className="perf-hub-preview-metric">💬 1.2k</span>
                  <span className="perf-hub-preview-pill connected">connected</span>
                </div>
              </div>
              <div className="perf-hub-preview-foot">
                4 creators · last sync 2 min ago
              </div>
            </div>
          </div>
        </div>
      </section>

      {MILAN_WORKSPACE_ENABLED ? (
        <>
          <section className="usp-section">
            <div className="section-container">
              <div className="usp-header">
                <span className="usp-badge">WHY MILAN AI?</span>
                <h2>The Only Platform with Performance-Based Creator Payments</h2>
                <p>Traditional agencies charge fixed fees regardless of results. We tie payments to actual Instagram performance.</p>
              </div>
              <div className="usp-grid">
                <div className="usp-card highlight">
                  <div className="usp-icon-wrapper highlight-icon"><span className="usp-icon">📊</span></div>
                  <div className="usp-number">01</div>
                  <h3>Dynamic Performance Pricing</h3>
                  <p>Fixed payments (60%) released weekly during monitoring (1-4 weeks). Performance payments (40%) released at end — based on actual engagement metrics. Pay for real results, not promises.</p>
                  <div className="usp-highlight-tag">INDUSTRY FIRST</div>
                </div>
                <div className="usp-card">
                  <div className="usp-icon-wrapper"><span className="usp-icon">🤖</span></div>
                  <div className="usp-number">02</div>
                  <h3>AI-Powered Creator Matching</h3>
                  <p>Our algorithm analyzes your campaign goals, budget, niche, and target audience to recommend creators most likely to deliver results.</p>
                </div>
                <div className="usp-card">
                  <div className="usp-icon-wrapper"><span className="usp-icon">🔄</span></div>
                  <div className="usp-number">03</div>
                  <h3>End-to-End Campaign Management</h3>
                  <p>From discovery to payment — manage invitations, content submissions, approvals, revisions, and performance tracking in one dashboard.</p>
                </div>
                <div className="usp-card">
                  <div className="usp-icon-wrapper"><span className="usp-icon">👤</span></div>
                  <div className="usp-number">04</div>
                  <h3>Human-in-Loop Validation</h3>
                  <p>Every content piece and creator selection is validated by you. Full control over approvals, up to 3 revision rounds, and content URL verification.</p>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="usp-section">
          <div className="section-container">
            <div className="usp-header">
              <span className="usp-badge">WHY PERFORMANCE HUB?</span>
              <h2>Clear Instagram ROI without changing how you run campaigns</h2>
              <p>Built for brands and agencies that already have creators — you need measurement, not another marketplace.</p>
            </div>
            <div className="usp-grid">
              <div className="usp-card highlight">
                <div className="usp-icon-wrapper highlight-icon"><span className="usp-icon">📥</span></div>
                <div className="usp-number">01</div>
                <h3>Your roster, your posts</h3>
                <p>Upload handle, email, and post URL. No creator discovery tax — track the partnerships you already negotiated.</p>
                <div className="usp-highlight-tag">AGENCY-FRIENDLY</div>
              </div>
              <div className="usp-card">
                <div className="usp-icon-wrapper"><span className="usp-icon">🔗</span></div>
                <div className="usp-number">02</div>
                <h3>One-click Instagram connect</h3>
                <p>Creators open a magic link and authorize once. No Milan signup for them; verified insights sync to your hub.</p>
              </div>
              <div className="usp-card">
                <div className="usp-icon-wrapper"><span className="usp-icon">📈</span></div>
                <div className="usp-number">03</div>
                <h3>Economics &amp; intelligence</h3>
                <p>CPM/CPE-style economics, Creative DNA, comment intelligence, and Post Guard — so you double down on winners.</p>
              </div>
              <div className="usp-card">
                <div className="usp-icon-wrapper"><span className="usp-icon">📋</span></div>
                <div className="usp-number">04</div>
                <h3>CRM &amp; export</h3>
                <p>Shortlist top performers privately and export CSV on paid plans for reporting to clients or finance.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="platforms-section-home">
        <div className="section-container">
          <h2 className="section-title">Supported Platforms</h2>
          <p className="section-subtitle">
            {MILAN_WORKSPACE_ENABLED
              ? 'Connect with creators on your favorite social media platforms'
              : 'Instagram tracking is live — more platforms on the way'}
          </p>
          <div className="home-platforms-grid">
            <div className="home-platform-card active">
              <div className="home-platform-icon instagram">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <h3>Instagram</h3>
              <p>
                {MILAN_WORKSPACE_ENABLED
                  ? 'Connect with verified Instagram creators. Track engagement, reach, and performance metrics.'
                  : 'Verified Instagram insights for posts you already briefed — likes, comments, reach, saves, and plays.'}
              </p>
              <span className="home-platform-badge active">Available Now</span>
            </div>
            <div className="home-platform-card coming-soon">
              <div className="home-platform-icon youtube">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <h3>YouTube</h3>
              <p>Long-form content partnerships with YouTube creators. Coming soon to Milan AI.</p>
              <span className="home-platform-badge coming-soon">Coming Soon</span>
            </div>
            <div className="home-platform-card coming-soon">
              <div className="home-platform-icon twitter">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </div>
              <h3>X (Twitter)</h3>
              <p>Tweet collaborations and thread campaigns with Twitter/X creators. Coming soon.</p>
              <span className="home-platform-badge coming-soon">Coming Soon</span>
            </div>
          </div>
          <div className="platforms-cta">
            <p>Want to be notified when new platforms are added?</p>
            <Link to="/contact" className="btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      {MILAN_WORKSPACE_ENABLED ? (
        <>
          <section className="pain-section">
            <div className="section-container">
              <h2 className="section-title">Does this sound familiar?</h2>
              <div className="pain-grid">
                <div className="pain-card">
                  <span className="pain-icon">📊</span>
                  <h3>Spreadsheet chaos</h3>
                  <p>Managing creators, deliverables, and payments across 10+ different Excel sheets</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">⏰</span>
                  <h3>Hours on outreach</h3>
                  <p>Copy-pasting the same DMs to hundreds of creators, losing track of responses</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">💸</span>
                  <h3>Agency commissions</h3>
                  <p>Paying 20-30% to agencies when you could work with creators directly</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">🔍</span>
                  <h3>Finding the right fit</h3>
                  <p>Scrolling endlessly through Instagram hoping to find creators who match your brand</p>
                </div>
              </div>
            </div>
          </section>

          <section className="cost-section">
            <div className="section-container">
              <div className="cost-content">
                <div className="cost-problem">
                  <span className="cost-badge negative">Traditional Approach</span>
                  <h3>Common challenges with influencer marketing</h3>
                  <ul className="cost-list">
                    <li><span className="cost-amount">High fees</span> from agencies and platforms</li>
                    <li><span className="cost-amount">Hours spent</span> on manual coordination</li>
                    <li><span className="cost-amount">Slow process</span> for campaign execution</li>
                    <li><span className="cost-amount">Limited visibility</span> into creator pricing</li>
                  </ul>
                  <div className="cost-total">
                    <span>Common pain points:</span>
                    <strong>Time & Money Drain</strong>
                  </div>
                </div>
                <div className="cost-solution">
                  <span className="cost-badge positive">The Milan AI Way</span>
                  <h3>Streamlined influencer marketing</h3>
                  <ul className="feature-check-list">
                    <li><span className="check">✓</span> Browse Instagram creators with verified data</li>
                    <li><span className="check">✓</span> AI-powered matching & recommendations</li>
                    <li><span className="check">✓</span> End-to-end campaign management</li>
                    <li><span className="check">✓</span> Secure escrow payment system</li>
                    <li><span className="check">✓</span> Real-time performance tracking</li>
                    <li><span className="check">✓</span> Content approval workflows</li>
                  </ul>
                  <div className="cost-savings">
                    <span>Platform access:</span>
                    <strong>Free</strong>
                    <small>Pay only 3.5% on completed campaigns</small>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="features-section">
            <div className="section-container">
              <h2 className="section-title">Complete Campaign Management Suite</h2>
              <p className="section-subtitle">Everything you need from discovery to performance tracking</p>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">🎯</div>
                  <h3>Smart Creator Discovery</h3>
                  <p>Find Instagram creators with advanced filters — niche, location, engagement rate, follower range, and pricing.</p>
                </div>
                <div className="feature-card featured">
                  <div className="feature-icon">🤖</div>
                  <h3>AI Creator Matching</h3>
                  <p>Our algorithm recommends creators based on your campaign goals, budget constraints, and target audience fit.</p>
                  <span className="feature-badge">AI Powered</span>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">📧</div>
                  <h3>Automated Invitations</h3>
                  <p>Send professional campaign briefs with timelines, deliverables, and compensation — with auto-expiry and backup activation.</p>
                </div>
                <div className="feature-card featured">
                  <div className="feature-icon">👤</div>
                  <h3>Human Validation</h3>
                  <p>You approve every creator selection and content piece. Up to 3 revision rounds before final approval.</p>
                  <span className="feature-badge">Full Control</span>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">💳</div>
                  <h3>Secure Escrow</h3>
                  <p>Funds held in escrow throughout monitoring (1-4 weeks). 60% fixed released weekly, 40% performance released at end.</p>
                </div>
                <div className="feature-card featured">
                  <div className="feature-icon">📊</div>
                  <h3>Live Performance Tracking</h3>
                  <p>Daily Instagram API integration tracks likes, comments, reach, saves, and video views automatically.</p>
                  <span className="feature-badge">Real Metrics</span>
                </div>
              </div>
            </div>
          </section>

          <section className="performance-section">
            <div className="section-container">
              <div className="performance-header">
                <span className="performance-badge">HOW IT WORKS</span>
                <h2>Performance-Based Payment System</h2>
                <p>The first platform where creator payments are directly tied to actual campaign results</p>
              </div>
              <div className="performance-flow">
                <div className="performance-step">
                  <div className="step-circle">1</div>
                  <div className="step-content">
                    <h4>Campaign Launch</h4>
                    <p>Funds held securely in escrow. Creator creates content following your brief.</p>
                  </div>
                </div>
                <div className="performance-arrow">→</div>
                <div className="performance-step">
                  <div className="step-circle">2</div>
                  <div className="step-content">
                    <h4>Content Approval</h4>
                    <p>You review and approve content (up to 3 revisions). <strong>Content goes live</strong>.</p>
                  </div>
                </div>
                <div className="performance-arrow">→</div>
                <div className="performance-step">
                  <div className="step-circle">3</div>
                  <div className="step-content">
                    <h4>Weekly Fixed Payments</h4>
                    <p>60% fixed payment released weekly during the monitoring period (1-4 weeks).</p>
                  </div>
                </div>
                <div className="performance-arrow">→</div>
                <div className="performance-step highlight-step">
                  <div className="step-circle highlight-circle">4</div>
                  <div className="step-content">
                    <h4>Performance Payment</h4>
                    <p><strong>40% performance-based</strong> released after monitoring based on actual metrics.</p>
                  </div>
                </div>
              </div>
              <div className="performance-comparison">
                <div className="comparison-card traditional">
                  <h4>❌ Traditional Method</h4>
                  <ul>
                    <li>Fixed payment upfront</li>
                    <li>No performance accountability</li>
                    <li>Pay same regardless of results</li>
                    <li>No refund for poor performance</li>
                  </ul>
                </div>
                <div className="comparison-card milan">
                  <h4>✅ Milan AI Method</h4>
                  <ul>
                    <li>Weekly payments during 1-4 week monitoring</li>
                    <li>Real Instagram API metrics</li>
                    <li>60% fixed weekly + 40% performance at end</li>
                    <li>Fair for brands & creators</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="stats-banner">
            <div className="section-container">
              <div className="stats-grid">
                <div className="banner-stat">
                  <span className="banner-value">₹0</span>
                  <span className="banner-label">Upfront Cost</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">3.5%</span>
                  <span className="banner-label">Success Fee Only</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">Instagram</span>
                  <span className="banner-label">Creator Network</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">∞</span>
                  <span className="banner-label">Unlimited Campaigns</span>
                </div>
              </div>
            </div>
          </section>

          <section className="how-section">
            <div className="section-container">
              <h2 className="section-title">Launch campaigns in minutes</h2>
              <p className="section-subtitle">Simple, streamlined workflow designed for speed and results</p>
              <div className="steps-grid">
                <div className="step-card">
                  <span className="step-number">1</span>
                  <h3>Discover Creators</h3>
                  <p>Use AI-powered search to find creators that match your brand, budget, and campaign goals.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">2</span>
                  <h3>Connect & Negotiate</h3>
                  <p>Message creators directly, discuss deliverables, and finalize partnerships on the platform.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">3</span>
                  <h3>Run Campaigns</h3>
                  <p>Track deliverables, approve content, manage timelines, and ensure quality with built-in tools.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">4</span>
                  <h3>Analyze & Scale</h3>
                  <p>Get AI-powered insights to optimize performance and scale what works for better ROI.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="explore-section">
            <div className="section-container">
              <div className="explore-content">
                <h2>Discover Instagram Creators</h2>
                <p>Find creators that match your brand with our AI-powered search and recommendations</p>
                <Link to={canFindCreators ? "/find-creators" : "/signup"} className="btn-primary large">
                  {canFindCreators ? "Browse Creators" : "Sign Up as Brand"}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="pain-section">
            <div className="section-container">
              <h2 className="section-title">Still tracking influencer ROI in spreadsheets?</h2>
              <div className="pain-grid">
                <div className="pain-card">
                  <span className="pain-icon">📊</span>
                  <h3>Scattered screenshots</h3>
                  <p>Clients ask for reach and engagement — you chase creators for Instagram Insights screenshots.</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">⏳</span>
                  <h3>Stale numbers</h3>
                  <p>By the time you compile a report, the metrics are already a week old.</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">❓</span>
                  <h3>Hard to compare</h3>
                  <p>Different formats, incomplete data — knowing who actually performed is guesswork.</p>
                </div>
                <div className="pain-card">
                  <span className="pain-icon">🔁</span>
                  <h3>Repeat winners?</h3>
                  <p>Without a shortlist and history, you redo the same analysis every campaign.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="how-section">
            <div className="section-container">
              <h2 className="section-title">Up and tracking in four steps</h2>
              <p className="section-subtitle">Designed for brands and agencies who already run the campaign elsewhere</p>
              <div className="steps-grid">
                <div className="step-card">
                  <span className="step-number">1</span>
                  <h3>Upload CSV</h3>
                  <p>Instagram handle, email, and post URL for each creator you want to measure.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">2</span>
                  <h3>Send connect links</h3>
                  <p>We email magic links. Creators connect Instagram once — no Milan account required.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">3</span>
                  <h3>Watch metrics sync</h3>
                  <p>Daily auto-sync plus manual refresh. Overview shows who is delivering.</p>
                </div>
                <div className="step-connector">→</div>
                <div className="step-card">
                  <span className="step-number">4</span>
                  <h3>Act on winners</h3>
                  <p>Shortlist to CRM, export CSV on paid plans, brief the next flight with confidence.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="stats-banner">
            <div className="section-container">
              <div className="stats-grid">
                <div className="banner-stat">
                  <span className="banner-value">₹0</span>
                  <span className="banner-label">Free tier to start</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">CSV</span>
                  <span className="banner-label">Your creator list</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">IG</span>
                  <span className="banner-label">Verified insights</span>
                </div>
                <div className="banner-stat">
                  <span className="banner-value">Daily</span>
                  <span className="banner-label">Auto sync</span>
                </div>
              </div>
            </div>
          </section>

          <section className="explore-section">
            <div className="section-container">
              <div className="explore-content">
                <h2>Ready to measure your next creator flight?</h2>
                <p>Open Performance Hub — free tier available, upgrade when your roster grows.</p>
                <Link to={primaryCta} className="btn-primary large">
                  {primaryCtaLabel}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      <section className="faq-section">
        <div className="section-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">
            {MILAN_WORKSPACE_ENABLED
              ? 'Everything you need to know about Milan AI'
              : 'Everything you need to know about Performance Hub'}
          </p>
          <div className="faq-list">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className={`faq-item ${openFaq === faq.id ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === faq.id ? '−' : '+'}</span>
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-container">
          {MILAN_WORKSPACE_ENABLED ? (
            <>
              <h2>Ready to streamline your influencer marketing?</h2>
              <p>Create campaigns, find creators, and track performance — all in one place</p>
              <div className="cta-buttons">
                <Link to={canFindCreators ? "/find-creators" : "/signup"} className="btn-primary large">
                  {canFindCreators ? "Find Creators" : "Get Started Free"}
                </Link>
                <Link to={loggedIn ? brandAppPath : "/signup"} className="btn-outline large">
                  {loggedIn ? "Go to Dashboard" : "Sign Up Free"}
                </Link>
              </div>
              <div className="cta-features">
                <span>✓ No credit card required</span>
                <span>✓ No upfront fees</span>
                <span>✓ Pay only on success</span>
              </div>
            </>
          ) : (
            <>
              <h2>Start tracking Instagram performance today</h2>
              <p>One workspace for your roster metrics — no escrow, no creator marketplace required</p>
              <div className="cta-buttons">
                <Link to={primaryCta} className="btn-primary large">
                  {primaryCtaLabel}
                </Link>
                <Link to="/pricing" className="btn-outline large">
                  View pricing
                </Link>
              </div>
              <div className="cta-features">
                <span>✓ Free tier available</span>
                <span>✓ Creators need no Milan login</span>
                <span>✓ Built for brands &amp; agencies</span>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
